import React, { useState } from "react";

// ─── FAQ Item ─────────────────────────────────────────────────────────────────
const FAQItem = ({ question, answer, isOpen, onClick, index }) => (
    <div
        className="border-b border-slate-100 last:border-0 animate-[cardIn_0.5s_ease_both]"
        style={{ animationDelay: `${index * 80}ms` }}
    >
        <button
            className="w-full py-6 flex justify-between items-center text-left focus:outline-none group"
            onClick={onClick}
        >
            <span
                className={`font-[Plus_Jakarta_Sans] font-semibold text-base md:text-lg transition-colors duration-200 ${
                    isOpen
                        ? "text-cyan-500"
                        : "text-slate-800 group-hover:text-cyan-500"
                }`}
            >
                {question}
            </span>
            <span
                className={`ml-4 flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center border transition-all duration-300 ${
                    isOpen
                        ? "bg-cyan-500 border-cyan-500 rotate-45"
                        : "bg-white border-slate-200 rotate-0 group-hover:border-cyan-300"
                }`}
            >
                <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className={`w-3.5 h-3.5 transition-colors duration-200 ${isOpen ? "text-white" : "text-slate-400"}`}
                >
                    <line x1="12" y1="5" x2="12" y2="19" />
                    <line x1="5" y1="12" x2="19" y2="12" />
                </svg>
            </span>
        </button>

        <div
            className={`overflow-hidden transition-all duration-300 ease-in-out ${
                isOpen ? "max-h-96 opacity-100" : "max-h-0 opacity-0"
            }`}
        >
            <p className="font-[DM_Sans] text-sm text-slate-500 leading-relaxed font-light pb-6 pr-12">
                {answer}
            </p>
        </div>
    </div>
);

// ─── Main Section ─────────────────────────────────────────────────────────────
const CareLineFAQSection = () => {
    const [openIndex, setOpenIndex] = useState(-1);

    const faqs = [
        {
            question: "Bagaimana Cara Mengambil Antrian?",
            answer: "Pasien dapat mengambil antrian langsung melalui website dengan mengisi data yang diperlukan.",
        },
        {
            question: "Apakah Bisa Ambil Antrian Lebih Dari Satu Kali?",
            answer: "Tidak. Setiap pasien hanya dapat mengambil satu antrian untuk mencegah duplikasi dan menjaga antrian tetap tertib.",
        },
        {
            question: "Bagaimana Jika Sudah Mengambil Antrian?",
            answer: "Jika antrian sudah diambil, sistem akan menampilkan informasi antrian Anda. Silakan cek beranda untuk melihat status antrian.",
        },
        {
            question: "Apa Sistem Antrian Ini Bisa Diakses Kapan Saja?",
            answer: "Ya. Website dapat diakses kapan saja, sehingga pasien dapat mengambil antrian tanpa harus datang langsung ke klinik.",
        },
    ];

    return (
        <>
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&family=DM+Sans:wght@300;400;500;600&display=swap');
                @keyframes cardIn {
                    from { opacity: 0; transform: translateY(20px); }
                    to   { opacity: 1; transform: translateY(0); }
                }
                /* ✅ Fix 2: pseudo-element untuk background fixed, tidak kena bug overflow-hidden */
                .faq-section::before {
                    content: '';
                    position: absolute;
                    inset: 0;
                    background-image: url('/patterns/careline-polyclinics-pattern.svg');
                    background-size: auto;
                    background-position: center;
                    background-attachment: fixed;
                    z-index: 0;
                }
            `}</style>

            <section
                className="faq-section relative bg-slate-50 overflow-hidden"
                style={{
                    padding:
                        "clamp(48px, 8vw, 96px) 0 clamp(64px, 10vw, 120px)",
                }}
            >
                {/* Decorative blobs */}
                <div className="absolute top-0 left-0 w-64 h-64 bg-cyan-100 rounded-full blur-3xl opacity-30 -translate-x-1/2 -translate-y-1/2 pointer-events-none" />
                <div className="absolute bottom-0 right-0 w-96 h-96 bg-teal-100 rounded-full blur-3xl opacity-25 translate-x-1/3 translate-y-1/3 pointer-events-none" />

                <div
                    className="relative z-10 mx-auto px-4 sm:px-6 lg:px-12"
                    style={{ maxWidth: "1200px" }}
                >
                    <div className="flex flex-col lg:flex-row gap-12 lg:gap-24">
                        {/* Left — Title */}
                        <div className="lg:w-1/3 pt-2">
                            <h2
                                className="font-[Plus_Jakarta_Sans] font-bold text-slate-900 leading-[1.15] tracking-[-0.02em] mb-5"
                                style={{
                                    fontSize: "clamp(1.75rem, 3.5vw, 2.75rem)",
                                }}
                            >
                                Tentang{" "}
                                <em className="not-italic text-transparent bg-clip-text bg-gradient-to-r from-[#06B6D4] to-[#0891B2]">
                                    Klinik
                                </em>{" "}
                                Kami
                            </h2>
                            <p className="font-[DM_Sans] text-slate-500 text-base leading-relaxed font-light mb-8 max-w-sm">
                                Temukan jawaban atas pertanyaan umum seputar
                                sistem antrian klinik kami. Jika membutuhkan
                                informasi lebih lanjut, tim kami siap membantu.
                            </p>

                            {/* Dot decoration */}
                            <div className="grid grid-cols-4 gap-2 w-24 opacity-20">
                                {Array.from({ length: 16 }).map((_, i) => (
                                    <div
                                        key={i}
                                        className="w-1.5 h-1.5 rounded-full bg-cyan-500"
                                    />
                                ))}
                            </div>
                        </div>

                        {/* Right — Accordion */}
                        <div className="lg:w-2/3">
                            <div className="bg-white rounded-3xl px-8 shadow-[0_4px_24px_rgba(15,23,42,0.07),0_1px_4px_rgba(15,23,42,0.04)] border border-slate-100">
                                {faqs.map((faq, index) => (
                                    <FAQItem
                                        key={index}
                                        index={index}
                                        question={faq.question}
                                        answer={faq.answer}
                                        isOpen={openIndex === index}
                                        onClick={() =>
                                            setOpenIndex(
                                                index === openIndex
                                                    ? -1
                                                    : index,
                                            )
                                        }
                                    />
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </>
    );
};

export default CareLineFAQSection;